import React from 'react';
import { Checkbox, DatePicker, Select } from 'antd';
import Icon from '@ant-design/icons';

import SortDefault from '@icons/dimensions/sort-default.svg';
import SortUp from '@icons/dimensions/sort-up.svg';
import { ESortOrder } from '@src/emp-3.0/types';

import {
  ICommonHeaderRendererProps,
  IDateHeaderRenderProps,
  ISelectableHeaderRendererProps,
  ISortIcon,
} from './typed';
import {
  CommonHeaderRendererWrapper,
  DateHeaderRendererWrapper,
  RowNumberHeaderRendererWrapper,
  SelectableHeaderRendererWrapper,
  CheckboxHeaderRendererWrapper,
  SortIconWrapper,
} from './styled';

const { RangePicker } = DatePicker;

export const SortIcon: React.FC<ISortIcon> = ({ sortOrder }) => {
  const Sorter = () => {
    if (sortOrder === ESortOrder.ASC) {
      return <SortUp />;
    } else if (sortOrder === ESortOrder.DESC) {
      return <SortUp style={{ transform: 'rotate(180deg)' }} />;
    }
    return <SortDefault />;
  };
  return <Icon component={Sorter} />;
};

export const CommonHeaderRenderer: React.FC<ICommonHeaderRendererProps> = (props) => {
  const { column } = props;
  const { title, dataKey, sortEnabled, sort, onSortChange, headerStyle } = column ?? {};
  const { key: sortKey, order: sortOrder } = sort ?? {};
  const visibleSort = sortKey === dataKey && sortOrder !== ESortOrder.NORMAL;

  const handleSortChange = () => {
    if (!sortEnabled) return;

    if (sortKey === dataKey) {
      // 0: normal, 1: desc, 2: asc
      onSortChange?.({ key: dataKey, order: (sortOrder + 1) % 3 });
    } else {
      onSortChange?.({ key: dataKey, order: ESortOrder.DESC });
    }
  };

  return (
    <CommonHeaderRendererWrapper
      sortEnabled={sortEnabled}
      onClick={handleSortChange}
      style={headerStyle}
    >
      <div style={{ marginRight: '10px' }}>{title}</div>
      {sortEnabled ? (
        <SortIconWrapper visible={visibleSort}>
          <SortIcon sortOrder={sort?.order} />
        </SortIconWrapper>
      ) : null}
    </CommonHeaderRendererWrapper>
  );
};

export const CheckboxHeaderRenderer: React.FC<ISelectableHeaderRendererProps> = (props) => {
  const { column } = props;
  const { selectedList = [], onChange, data = [] } = column ?? {};

  const dataIds = new Set(data.map((item) => item.id));
  const selectedIds = new Set(selectedList.map((item) => item.id));

  const checked = selectedList.length > 0 && data.every((item) => selectedIds.has(item.id));
  const indeterminate = selectedList.length > 0 && selectedList.length < data.length;

  return (
    <CheckboxHeaderRendererWrapper>
      <Checkbox
        checked={checked}
        onChange={(e) => {
          const newList = e.target.checked
            ? [...data]
            : selectedList.filter((item) => !dataIds.has(item.id));

          onChange?.({ selectedList: [...newList] });
        }}
        indeterminate={indeterminate}
      />
    </CheckboxHeaderRendererWrapper>
  );
};

export const RowNumberHeaderRenderer = () => {
  return <RowNumberHeaderRendererWrapper>#</RowNumberHeaderRendererWrapper>;
};

export const SelectableHeaderRenderer: React.FC<ISelectableHeaderRendererProps> = (props) => {
  const { column } = props;
  const { showFilter, options, value, onChange } = column ?? {};

  if (!showFilter) return <CommonHeaderRenderer {...props} />;

  return (
    <SelectableHeaderRendererWrapper>
      <CommonHeaderRenderer {...props} />
      <Select
        showSearch
        options={options}
        dropdownMatchSelectWidth={false}
        style={{ marginTop: '4px', width: '100%' }}
        value={value}
        onChange={onChange}
      />
    </SelectableHeaderRendererWrapper>
  );
};

export const DateHeaderRender: React.FC<IDateHeaderRenderProps> = (props) => {
  const { column } = props;
  const { showFilter, value, onChange } = column ?? {};

  if (!showFilter) return <CommonHeaderRenderer {...props} />;

  const handleRangePickerChange = (date) => {
    if (date?.[0] && date?.[1]) {
      onChange([date[0].startOf('day'), date[1].endOf('day')]);
    } else {
      onChange([]);
    }
  };

  return (
    <DateHeaderRendererWrapper>
      <CommonHeaderRenderer {...props} />
      <RangePicker onChange={handleRangePickerChange} value={value} style={{ marginTop: '4px' }} />
    </DateHeaderRendererWrapper>
  );
};
